import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldQuestion, Eye, Paperclip, GitBranch, RefreshCw, AlertOctagon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { api, apiError, API } from "@/lib/api";
import { fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Canal de denuncia interna (43ª-VIII / 49ª) — bandeja confidencial del destinatario.
const ESTATUS = ["Recibida", "En análisis", "Escalada", "Convertida en caso", "Cerrada"];

export default function BandejaDenuncias() {
  const { user } = useAuth();
  const esOC = user?.role === "oficial_cumplimiento";
  const esAlterna = user?.role === "admin_entidad";
  const puede = esOC || esAlterna;

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const [nuevoEstatus, setNuevoEstatus] = useState("");
  const [nota, setNota] = useState("");

  const load = useCallback(async () => {
    if (!puede) return;
    try {
      const { data } = await api.get("/pld/denuncias");
      setRows(data.denuncias || []);
    } catch (e) { /* sin acceso */ }
  }, [puede]);
  useEffect(() => { load(); }, [load]);

  const abrir = async (row) => {
    try {
      const { data } = await api.get(`/pld/denuncias/${row.id}`);
      setSel(data); setNuevoEstatus(data.estatus); setNota("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const guardarEstatus = async () => {
    setBusy(true);
    try {
      await api.patch(`/pld/denuncias/${sel.id}/estatus`, { estatus: nuevoEstatus, nota });
      toast.success("Estatus actualizado.");
      setSel(null); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const convertir = async () => {
    if (!window.confirm("¿Convertir esta denuncia en un caso PLD para análisis formal?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/pld/denuncias/${sel.id}/convertir-caso`);
      toast.success(`Convertida en caso ${data.folio}.`);
      setSel(null); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const descargarAdjunto = async () => {
    try {
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}/pld/denuncias/${sel.id}/adjunto`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("No se pudo descargar el adjunto.");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = sel.adjunto_nombre || "adjunto";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
  };

  if (!puede) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="denuncias-restringido">
        <ShieldQuestion className="h-5 w-5" /> El canal de denuncias es estrictamente confidencial. Solo el Oficial de Cumplimiento (o el órgano de administración en el caso de denuncias contra el propio OC) accede a su contenido.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bandeja-denuncias">
      <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 p-4 text-xs text-sky-900">
        <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          {esAlterna
            ? "Vía alterna del canal de denuncia (43ª-VIII): aquí solo aparecen las denuncias dirigidas al ÓRGANO DE ADMINISTRACIÓN por ser contra el propio Oficial de Cumplimiento. # REVISIÓN HUMANA: define el destinatario alterno según el gobierno corporativo de la entidad."
            : "Bandeja confidencial del Oficial de Cumplimiento (43ª-VIII / 49ª). La identidad del denunciante anónimo NO se almacena. Todo acceso a una denuncia queda en bitácora."}
        </p>
      </div>

      <div className="flex justify-end">
        <Button data-testid="reload-denuncias-btn" size="sm" variant="outline" onClick={load}>
          <RefreshCw className="mr-1.5 h-4 w-4" /> Actualizar
        </Button>
      </div>

      <DataTable testid="table-denuncias" rows={rows}
        columns={[
          { key: "folio", label: "Folio" },
          { key: "fecha", label: "Fecha", render: (r) => fdate(r.fecha) },
          { key: "anonimo", label: "Tipo", render: (r) => r.anonimo ? <Badge variant="outline">Anónima</Badge> : <Badge variant="outline">Identificada</Badge> },
          { key: "area_senalada", label: "Área señalada", render: (r) => r.area_senalada || "—" },
          { key: "estatus", label: "Estatus", render: (r) => <StatusBadge value={r.estatus} /> },
        ]}
        actions={(row) => (
          <Button data-testid={`ver-denuncia-${row.id}`} size="sm" variant="outline" onClick={() => abrir(row)}>
            <Eye className="mr-1.5 h-4 w-4" /> Revisar
          </Button>
        )}
        emptyLabel="Sin denuncias en el canal." />

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="denuncia-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Denuncia {sel.folio}</DialogTitle>
                <DialogDescription>
                  {sel.anonimo ? "Denunciante ANÓNIMO (identidad no almacenada)" : `Denunciante: ${sel.denunciante?.nombre || ""} (${sel.denunciante?.email || ""})`}
                  {sel.contra_oc && " · Denuncia contra el Oficial de Cumplimiento (vía alterna)"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div><p className="text-xs text-slate-500">Descripción</p><p className="whitespace-pre-wrap text-slate-800">{sel.descripcion}</p></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-slate-500">Área señalada</p><p>{sel.area_senalada || "—"}</p></div>
                  <div><p className="text-xs text-slate-500">Persona señalada</p><p>{sel.persona_senalada || "—"}</p></div>
                </div>
                {sel.tiene_adjunto && (
                  <Button data-testid="descargar-adjunto-btn" size="sm" variant="outline" onClick={descargarAdjunto}>
                    <Paperclip className="mr-1.5 h-4 w-4" /> Descargar adjunto ({sel.adjunto_nombre})
                  </Button>
                )}
                <div>
                  <p className="text-xs text-slate-500">Historial</p>
                  <ul className="mt-1 space-y-1">
                    {(sel.historial || []).map((h, i) => (
                      <li key={i} className="text-xs text-slate-600">• {fdate(h.fecha)} — <strong>{h.estatus}</strong> {h.por ? `por ${h.por}` : ""} {h.nota ? `· ${h.nota}` : ""}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Gestión</p>
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <p className="mb-1 text-xs text-slate-500">Cambiar estatus</p>
                      <Select value={nuevoEstatus} onValueChange={setNuevoEstatus}>
                        <SelectTrigger data-testid="estatus-select" className="w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>{ESTATUS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Textarea data-testid="estatus-nota" placeholder="Nota (opcional)" className="h-9 flex-1 min-w-[180px]" value={nota} onChange={(e) => setNota(e.target.value)} />
                  </div>
                </div>
              </div>
              <DialogFooter className="gap-2">
                {esOC && !sel.caso_id && (
                  <Button data-testid="convertir-caso-btn" variant="outline" disabled={busy} onClick={convertir}>
                    <GitBranch className="mr-1.5 h-4 w-4" /> Convertir en caso PLD
                  </Button>
                )}
                <Button data-testid="guardar-estatus-btn" disabled={busy} onClick={guardarEstatus} className="bg-primary hover:bg-sidebar-hover">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Guardar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
